import { Box, Divider, Flex, Group, Select } from '@byecode/ui'
import { FlowSelectSourceType } from '@lighthouse/core'
import {
    type FlowNode,
    getFieldOptions,
    getPrimaryDsSystemFieldId,
    getUpstreamRealDsId,
    type WeChatPayActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { SwitchControl } from '@/components/BlockSettings/Common/SwitchControl'
import { FieldSelect } from '@/components/FieldSelect'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { SettingRecordContent } from '@/containers/FlowDetail/components/SettingRecordContent'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource } from '@/hooks/useDataSource'

import * as CM from '../../styles'
import { EventSuccessAfter } from '../component/EventSuccessAfter'

interface PayProps {
    allParentNodes?: FlowNode[]
    value: WeChatPayActionConfig
    onChange: (value: WeChatPayActionConfig) => void
}

const EmptyArray: FlowNode[] = []

const Pay: React.FunctionComponent<PayProps> = props => {
    const { allParentNodes = EmptyArray, value, onChange } = props

    const { type } = useFlowContainerContext()
    const isWorkflow = Boolean(type)
    const {
        nodeId,
        dataSourceId: dsId,
        selectType,
        descFieldId,
        amountFieldId,
        autoRedirectAfterPayment,
        profitSharingFieldId,
        orderTimeExpire,
        succeedFieldSettings,
        callbackWorkflow
    } = value

    const dataSourceId = useMemo(() => {
        if (selectType === FlowSelectSourceType.UPSTREAM) {
            return getUpstreamRealDsId(nodeId, allParentNodes)
        }
        return dsId
    }, [allParentNodes, dsId, nodeId, selectType])

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSource = useDataSource(appId, envId, dataSourceId ?? '')

    const { schema, viewOptions, type: dsType } = dataSource || {}
    const { tableProps = [] } = viewOptions || {}
    const lookTextFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.innerType === 'TEXT')
    }, [schema, tableProps])

    const lookNumberFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.innerType === 'NUMBER')
    }, [schema, tableProps])

    const lookBoolFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.innerType === 'BOOL')
    }, [schema, tableProps])

    const lookTimeFieldOptions = useMemo(() => {
        if (!schema) {
            return []
        }
        return getFieldOptions(schema, tableProps, field => field.innerType === 'DATE')
    }, [schema, tableProps])

    const primaryDsSystemFieldId = useMemo(() => {
        if (!schema || !viewOptions || !dsType) {
            return
        }
        return getPrimaryDsSystemFieldId('ID', schema, viewOptions, dsType)
    }, [schema, viewOptions, dsType])

    if (!dataSource) {
        return null
    }
    return (
        <>
            <Group label="支付订单信息">
                <CM.FieldList>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="orderId"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>订单号</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <Select
                            value={primaryDsSystemFieldId}
                            options={lookTextFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            disabled
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="shopDescribe"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>商品描述</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />

                        <FieldSelect
                            options={lookTextFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从支付订单中选择字段"
                            value={descFieldId}
                            onChange={val => onChange({ ...value, descFieldId: val })}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="orderMoney"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle required>订单金额 （元）</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <FieldSelect
                            options={lookNumberFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从支付订单中选择字段"
                            value={amountFieldId}
                            onChange={val => onChange({ ...value, amountFieldId: val })}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="orderWillProfitSharing"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle>是否为分账订单</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <FieldSelect
                            options={lookBoolFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从支付订单中选择字段"
                            value={profitSharingFieldId}
                            onChange={val => onChange({ ...value, profitSharingFieldId: val })}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <PopoverQuestion
                            type="orderTimeExpire"
                            renderTarget={(node, ref) => (
                                <Flex gap={16} ref={ref} alignItems="center">
                                    <CM.FieldTitle>支付结束时间</CM.FieldTitle>
                                    {node}
                                </Flex>
                            )}
                        />
                        <FieldSelect
                            options={lookTimeFieldOptions}
                            styles={{
                                root: {
                                    width: '100%'
                                }
                            }}
                            placeholder="从支付订单中选择字段"
                            value={orderTimeExpire}
                            onChange={val => onChange({ ...value, orderTimeExpire: val })}
                        />
                    </CM.FieldItem>
                </CM.FieldList>
            </Group>
            <Divider />
            <Group label="支付成功后更新订单">
                <Box px={8}>
                    <SettingRecordContent
                        dataSourceId={dataSource.id}
                        disablePageLink
                        disableFormOption
                        disableViewOption
                        disablePageOption
                        value={succeedFieldSettings || []}
                        onChange={val => onChange({ ...value, succeedFieldSettings: val })}
                    />
                </Box>
            </Group>
            <Divider />
            <EventSuccessAfter
                title="支付成功后的行为"
                prefixNode={
                    <SwitchControl
                        label="跳转回原页面"
                        disabled={!isWorkflow}
                        checked={autoRedirectAfterPayment}
                        onChange={e => onChange({ ...value, autoRedirectAfterPayment: e.target.checked })}
                    />
                }
                dataSource={dataSource}
                allParentNodes={allParentNodes}
                value={callbackWorkflow}
                onChange={val => onChange({ ...value, callbackWorkflow: val })}
            />

            {/* <Divider />
            <Group label="支付失败后更新订单">
                <SettingRecordContent
                    prefixName={`${prefixName}`}
                    fieldsName="failedFieldSettings"
                    allParentNodes={allParentNodes}
                    dataSourceId={dataSource.id}
                />
            </Group> */}
        </>
    )
}

export default Pay
